import React from 'react';
import { ChartWidget } from './ChartWidget';
import './ChartWidget.scss';

interface WidgetSpec {
  widget_id: string;
  widget_type: string;
  spec: any;
  rendering_hints?: any;
}

interface WidgetRendererProps {
  widget: WidgetSpec;
  onAction?: (actionType: string, parameters: any) => void;
}

export const WidgetRenderer: React.FC<WidgetRendererProps> = ({ widget, onAction }) => {
  const renderWidget = () => {
    const { widget_type, spec } = widget;

    switch (widget_type) {
      case 'chart':
        return <ChartWidget spec={{ ...spec, widget_id: widget.widget_id }} onAction={onAction} />;

      case 'table':
        return (
          <div className="table-widget">
            {spec.title && <h4 className="table-title">{spec.title}</h4>}
            <table>
              <thead>
                <tr>
                  {spec.columns?.map((col: any, idx: number) => (
                    <th key={idx}>{col.label || col.name || col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {spec.data?.map((row: any, rowIdx: number) => (
                  <tr key={rowIdx}>
                    {spec.columns?.map((col: any, colIdx: number) => {
                      const key = col.key || col.name || col;
                      return <td key={colIdx}>{row[key]}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );

      case 'card':
        return (
          <div className="card-widget">
            {spec.title && <div className="card-title">{spec.title}</div>}
            <div className="card-value">
              {spec.value}
              {spec.unit && <span style={{ fontSize: '1rem', marginLeft: '5px' }}>{spec.unit}</span>}
            </div>
            {spec.trend && (
              <div className={`card-trend ${spec.trendValue && spec.trendValue > 0 ? 'positive' : 'negative'}`}>
                {spec.trend} {spec.trendValue && `(${spec.trendValue > 0 ? '+' : ''}${spec.trendValue}%)`}
              </div>
            )}
          </div>
        );

      case 'list':
        return (
          <div className="list-widget">
            {spec.title && <h4 className="list-title">{spec.title}</h4>}
            {spec.items?.map((item: any, idx: number) => (
              <div
                key={idx}
                className="list-item"
                onClick={() => onAction && onAction('view_detail', { item_id: item.id || idx })}
              >
                {item.title && <div className="item-title">{item.title}</div>}
                {item.description && <div className="item-description">{item.description}</div>}
                {typeof item === 'string' && <div className="item-title">{item}</div>}
              </div>
            ))}
          </div>
        );

      default:
        return (
          <div className="widget-error">
            <p>Unknown widget type: {widget_type}</p>
            <pre>{JSON.stringify(spec, null, 2)}</pre>
          </div>
        );
    }
  };

  return (
    <div className="widget-container" data-widget-id={widget.widget_id}>
      {renderWidget()}
    </div>
  );
};
